import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { isArray } from 'lodash';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { DAY_OFF_JOB_TYPES } from '@ubeya/shared/constants/timesheet';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { trackEvent } from '../../analytics/amplitude';
import ConfigContext from '../../contexts/ConfigContext';
import Select from '../Select';
import { DatePickerInput } from '../DatePicker';
import CheckBox from '../CheckBox';
import { EditableInput } from '../Input';
import EditableNotes from '../Input/EditableNotes';
import { TextWithTooltip } from '../Tooltip';
import Popover from '../Popover';
import { FlexMiddle } from '../Flex';
import { ReactComponent as ArrowIcon } from '../../assets/arrow-right-small.svg';
import CheckedRenderer from './CheckedRenderer';

const READ_ONLY_TOOLTIP_MAX_WIDTH = 700;

// Hand cursor on hover, consistent with the Clock In field behavior.
const EditableCell = styled.div`
  cursor: pointer;
  width: 100%;

  .editable-input {
    cursor: text;
  }
`;

const DateField = styled(DatePickerInput)`
  padding: 4px 6px;

  input {
    min-height: 34px;
  }
`;

const OptionsTrigger = styled(FlexMiddle)`
  cursor: pointer;
  width: 100%;
  height: 100%;
  min-height: 20px;
  justify-content: space-between;
  gap: 4px;
`;

const OptionsTriggerText = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  /* Let clicks fall through to the trigger so the text area also opens the popover. */
  pointer-events: none;
`;

// Same dropdown arrow as the Select component; points down when closed, up when open.
const Caret = styled(ArrowIcon)`
  flex-shrink: 0;
  width: 10px;
  pointer-events: none;
  transform: rotate(${({ $isOpen }) => ($isOpen ? 270 : 90)}deg);
  transition: 200ms;
`;

const OptionsPopoverContent = styled.div`
  min-width: 240px;
  padding: 8px;
`;

const normalizeOptionValue = (value) => (value === undefined || (isArray(value) && value.length === 0) ? null : value);

const isSameOptionValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Click-to-edit dropdown cell: shows a compact summary; clicking opens the full Select
 * in a floating popover (so multi-select chips/menu have room without growing the row).
 * Both single- and multi-select update a local draft on change and commit on close
 * (outside click), so the picked value is visible in the open popover before it saves.
 */
const OptionsEditor = ({ field, rawValue, formatValue, onCommit }) => {
  const isMulti = !!field.allowMultiple;
  const options = useMemo(() => (field.options || []).map(({ id, title }) => ({ value: id, label: title })), [
    field.options
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(rawValue);
  const selectRef = useRef();

  // Sync the draft with the latest stored value whenever the popover is closed.
  useEffect(() => {
    if (!isOpen) {
      setDraft(rawValue);
    }
  }, [rawValue, isOpen]);

  // Open the Select's menu as soon as the popover appears, so it's one click to edit.
  useEffect(() => {
    if (isOpen) {
      selectRef.current?.setOpen?.(true);
    }
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    const normalized = normalizeOptionValue(draft);
    if (!isSameOptionValue(normalized, rawValue)) {
      onCommit(normalized);
    }
  };

  // Update the draft only; both single and multi commit on close (outside click), so the
  // user can see the selected value reflected in the open popover before it saves.
  const handleChange = (value) => {
    setDraft(normalizeOptionValue(value));
  };

  const summary = formatValue ? formatValue(rawValue) : rawValue;

  return (
    <Popover
      isPopoverOpen={isOpen}
      onClickOutside={close}
      // Capture-phase outside detection: the virtualized table cells stopPropagation on
      // bubble, so a bubble-phase listener never sees outside clicks and wouldn't close.
      clickOutsideCapture
      positions={['bottom', 'top']}
      showArrow={false}
      containerStyle={{ zIndex: 6, top: '2px' }}
      popoverContent={
        <OptionsPopoverContent>
          <Select
            ref={selectRef}
            options={options}
            value={isMulti ? draft || [] : draft}
            isMulti={isMulti}
            clearable
            onChange={handleChange}
          />
        </OptionsPopoverContent>
      }
      triggerContent={
        // stopPropagation so the opening click doesn't immediately reach the popover's
        // outside-click listener (which would close it on the same click). Open-only;
        // closing happens on outside click or after a single-select pick.
        <OptionsTrigger
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}>
          <OptionsTriggerText>
            <TextWithTooltip tooltipBodyMaxWidth={READ_ONLY_TOOLTIP_MAX_WIDTH}>{summary}</TextWithTooltip>
          </OptionsTriggerText>
          <Caret $isOpen={isOpen} />
        </OptionsTrigger>
      }
    />
  );
};

/**
 * Inline editor for a single shift task (sequence) field value on a timesheet row.
 * Renders the matching control per field type (text/number/date/dropdown/checkbox),
 * commits via columnData.onEdit, and falls back to a read-only value when the admin
 */
const TimesheetTaskFieldRenderer = ({ rowData, columnData }) => {
  const { field, onEdit, canEdit, dateFormat, formatValue, amplitudeModuleName } = columnData;
  const { t } = useContext(ConfigContext);
  const { addSuccess } = useToaster();
  const { timesheetId, subRows, summarized, isExternal, isPto, isJob, jobTypeId, approved } = rowData || {};

  const rawValue = rowData?.mappedTimesheetFieldsValues?.[field.id];

  const [inputValue, setInputValue] = useState(rawValue ?? '');
  // Tracks the value we last persisted so a second submit for the same value (e.g. Enter
  // then blur) doesn't fire a duplicate update; kept in sync with the stored value.
  const lastCommittedRef = useRef(rawValue);

  useEffect(() => {
    setInputValue(rawValue ?? '');
    lastCommittedRef.current = rawValue;
  }, [rawValue]);

  // PTO, external employees and day-off jobs (sick/vacation) do not have editable shift task (timesheet) fields here.
  // Once the timesheet is approved, shift task values can feed payroll, so they're locked from further edits too.
  const readOnly =
    !canEdit ||
    isPto ||
    isExternal ||
    (isJob && !!DAY_OFF_JOB_TYPES[jobTypeId]) ||
    !!subRows ||
    !!summarized ||
    !timesheetId ||
    field.isDeleted ||
    !!approved;

  const commit = useCallback(
    async (value) => {
      try {
        await onEdit({ timesheetId, fieldId: field.id, value });
        addSuccess(t('timesheetTaskFieldUpdatedSuccess'));
        trackEvent('timesheet_task_field_edited', {
          field_id: field.id,
          field_type_id: field.fieldTypeId,
          timesheet_id: timesheetId,
          module: amplitudeModuleName
        });
      } catch (error) {
        // The optimistic mutation rolls back and surfaces the error toaster on failure
      }
    },
    [onEdit, timesheetId, field.id, field.fieldTypeId, amplitudeModuleName, addSuccess, t]
  );

  // special case for boolean fields
  if (readOnly) {
    if (field.fieldTypeId === FieldTypes.FIELD_TYPE_BOOLEAN) {
      return (
        <CheckedRenderer
          rowData={rowData}
          dataKey={`mappedTimesheetFieldsValues.${field.id}`}
          showFalse
          showTooltipForFalse
          showDashSignForFalse
        />
      );
    }

    return (
      <TextWithTooltip tooltipBodyMaxWidth={READ_ONLY_TOOLTIP_MAX_WIDTH}>
        {formatValue ? formatValue(rawValue) : rawValue}
      </TextWithTooltip>
    );
  }

  // Commit a typed value once. Compares against the last committed value (string-wise, so
  // 5 vs '5' isn't a change) so Enter-then-blur doesn't persist the same edit twice.
  const submitValue = (value) => {
    const next = value === '' ? null : value;
    if (String(next ?? '') !== String(lastCommittedRef.current ?? '')) {
      lastCommittedRef.current = next;
      commit(next);
    }
  };

  switch (field.fieldTypeId) {
    case FieldTypes.FIELD_TYPE_DATE:
      return (
        <EditableCell>
          <DateField
            value={rawValue || null}
            onChange={(value) => commit(value || null)}
            format={dateFormat}
            usePortal
          />
        </EditableCell>
      );

    case FieldTypes.FIELD_TYPE_OPTIONS:
      return <OptionsEditor field={field} rawValue={rawValue} formatValue={formatValue} onCommit={commit} />;

    case FieldTypes.FIELD_TYPE_BOOLEAN:
      return (
        <EditableCell>
          <CheckBox name={`tsf-${timesheetId}-${field.id}`} value={!!rawValue} onChange={(value) => commit(value)} />
        </EditableCell>
      );

    case FieldTypes.FIELD_TYPE_NUMBER:
      return (
        <EditableCell>
          {/* setEmptyWhenZero=false so focusing a 0 doesn't blank it and persist null on blur. */}
          <EditableInput
            type="number"
            value={inputValue}
            onChange={setInputValue}
            onSubmit={() => submitValue(inputValue)}
            setEmptyWhenZero={false}
          />
        </EditableCell>
      );

    case FieldTypes.FIELD_TYPE_TEXT:
    default: {
      const max = field?.metadata?.maxNumberOfChar ? Number(field.metadata.maxNumberOfChar) : undefined;
      const onChangeText = (text) => submitValue(max ? String(text ?? '').slice(0, max) : text ?? '');

      return (
        <EditableCell>
          <EditableNotes
            key={`tsf-${field.id}-${rawValue ?? ''}`}
            defaultValue={rawValue ?? ''}
            placeholder=""
            onChange={onChangeText}
          />
        </EditableCell>
      );
    }
  }
};

export default TimesheetTaskFieldRenderer;
